import { useQuery } from '@tanstack/react-query';

import { useAuth0Context } from '#src/state/contexts/auth0-context.js';
import { usePdfObjectUrlFromBytes } from '#utils/binary-data-url.js';
import { FileContentsRequestError, fileContentsQueryOptions, InvalidPdfError } from '#utils/file-contents-query.js';

import type { PdfObjectUrlGlobal } from '#utils/binary-data-url.js';

/**
 * The mutually-exclusive display states of the document a datasheet popover shows, derived from the
 * file fetch.
 *
 * A missing file (`not-found`) and one whose bytes are not a PDF (`invalid-pdf`) are told apart, so
 * a caller can keep its surrounding chrome on screen and offer a replacement for either; any other
 * fetch failure still throws to the surrounding error boundary.
 */
export type DatasheetDocumentState =
	| { status: 'loading' }
	| { status: 'empty' }
	| { status: 'error' }
	| { status: 'not-found' }
	| { status: 'invalid-pdf' }
	| { status: 'ready'; dataUrl: string };

/** The display state plus the stored file name, when the fetch has resolved. */
export type DatasheetDocumentData = DatasheetDocumentState & { fileName: string | undefined };

/**
 * Fetches file contents by ID and derives the popover's display state, read inline so the stored
 * file name is available before the bytes are.
 * @param fileId - The file ID to fetch, or null/undefined to skip (yields `empty`).
 * @param objectUrlWindow - The window to create the PDF blob URL in, or null to use the current window.
 * @returns The discriminated viewer state, plus the stored file name when known.
 */
export function useDatasheetPopoverData(
	fileId: string | null | undefined,
	objectUrlWindow: PdfObjectUrlGlobal | null = null,
): DatasheetDocumentData {
	const auth0 = useAuth0Context();

	const { data, error } = useQuery({
		...fileContentsQueryOptions(auth0.client, fileId),
		throwOnError: (err: unknown) => !FileContentsRequestError.isFileNotFound(err) && !InvalidPdfError.isInvalidPdf(err),
	});

	const fileName = data?.fileName ?? (error instanceof InvalidPdfError ? error.fileName : undefined);
	const dataUrl = usePdfObjectUrlFromBytes(data?.contents, objectUrlWindow);

	if (fileId === null || fileId === undefined) {
		return { status: 'empty', fileName };
	}
	if (error !== null) {
		if (InvalidPdfError.isInvalidPdf(error)) {
			return { status: 'invalid-pdf', fileName };
		}
		return { status: FileContentsRequestError.isFileNotFound(error) ? 'not-found' : 'error', fileName };
	}
	if (dataUrl === undefined) {
		return { status: 'loading', fileName };
	}
	return { status: 'ready', dataUrl, fileName };
}
